import React from "react";
import { Box } from "@mui/joy";
import { theme } from "styles/theme";

// =========================
// AgentLivingLogo — "살아있는 뇌" (top-down view) MBCT/CBT × Entity feel
//   · 뇌 실루엣 (좌우 hemisphere) — 3겹으로 그려서 와이어 두께감
//   · 종단 fissure — 중앙 골이 호흡하며 흐름
//   · gyri 곡선 (뇌주름) — 각 hemisphere 안에서 살짝 흔들림
//   · 내부에 떠도는 신경 입자 12개 — 다른 속도, 뇌 안 범위 안에 머무름
//   · 중심 코어 — 다층 halo + flicker (의식의 가운데)
//   · 가끔 sweep 라인이 한 바퀴 돎 (알아차림 스캔)
//   모든 timing 이 lockstep 이 아니라 organic.
// =========================

const BRAIN_OUTLINE =
  "M 40 12 C 30 9, 17 14, 12 26 C 7 38, 10 52, 19 60 C 26 66, 35 66, 40 62 C 45 66, 54 66, 61 60 C 70 52, 73 38, 68 26 C 63 14, 50 9, 40 12 Z";

const BRAIN_FISSURE = "M 40 12 C 38 22, 42 30, 40 40 C 38 50, 42 58, 40 62";

const GYRI_LEFT = [
  "M 16 30 Q 22 27, 28 31 T 36 29",
  "M 14 42 Q 22 39, 28 44 T 36 42",
  "M 18 52 Q 24 49, 30 54",
  "M 20 22 Q 26 19, 32 22",
];

const GYRI_RIGHT = [
  "M 64 30 Q 58 27, 52 31 T 44 29",
  "M 66 42 Q 58 39, 52 44 T 44 42",
  "M 62 52 Q 56 49, 50 54",
  "M 60 22 Q 54 19, 48 22",
];

export function AgentLivingLogo({
  size = 44,
  active = true,
}: {
  size?: number;
  active?: boolean;
}) {
  const primary = theme.vars.palette.primary.solid;
  const soft = theme.vars.palette.primary.soft;

  // 16 신경 입자 — 각자 고유한 chaotic trajectory
  //   주요 회전 + 부 sinusoidal 각도 jitter + 반경 진동 합성
  //   서로 다른 주기/위상이 겹쳐서 lockstep 절대 안 됨
  const particles = Array.from({ length: 16 }, (_, i) => ({
    baseR: 6 + ((i * 2.7) % 14), // 기본 반경
    rAmp: 2 + ((i * 0.8) % 5), // 반경 진동 amplitude
    rFreq: 1 + (i % 3), // 반경 sinusoid 주파수 (정수, loop 매끄럽게)
    angAmp: 18 + ((i * 7) % 32), // 각속도 비균질성 (degrees)
    angFreq: 1 + ((i + 1) % 3), // 각도 jitter 주파수 (정수)
    dur: 5.5 + ((i * 1.3) % 9), // 한 cycle 시간
    startAngle: (i * 137.5) % 360, // golden-angle spacing
    phase: (i * 0.41) % (Math.PI * 2), // sinusoid 위상 offset
    size: 0.45 + ((i * 0.13) % 0.7),
    opacity: 0.4 + ((i * 0.07) % 0.5),
    dir: i % 2 === 0 ? 1 : -1,
  }));

  // 각 입자별 keyframes 생성 — 12 stops 의 chaotic 궤도
  const particleAnimations: Record<string, unknown> = {};
  const NUM_STOPS = 12;
  particles.forEach((p, i) => {
    const kfName = `agent-pt-${i}`;
    const stops: Record<string, { transform: string }> = {};
    for (let j = 0; j <= NUM_STOPS; j++) {
      const t = j / NUM_STOPS;
      // 각속도: 균일 회전 + sinusoidal jitter
      const angleDeg =
        p.startAngle +
        t * 360 * p.dir +
        Math.sin(t * Math.PI * 2 * p.angFreq + p.phase) * p.angAmp;
      // 반경: base + sinusoidal 진동
      const r =
        p.baseR + Math.sin(t * Math.PI * 2 * p.rFreq + p.phase * 1.7) * p.rAmp;
      const rad = (angleDeg * Math.PI) / 180;
      const x = r * Math.cos(rad);
      const y = r * Math.sin(rad);
      stops[`${Math.round(t * 100)}%`] = {
        transform: `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`,
      };
    }
    particleAnimations[`@keyframes ${kfName}`] = stops;
  });

  return (
    <Box
      component="svg"
      viewBox="0 0 80 80"
      sx={{
        width: size * (80 / 44),
        height: size * (80 / 44),
        overflow: "visible",
        ...particleAnimations,
        "@keyframes agent-brain-breath": {
          "0%, 100%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.035)" },
        },
        "@keyframes agent-brain-breath-slow": {
          "0%, 100%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.02)" },
        },
        "@keyframes agent-gyri-fade": {
          "0%, 100%": { opacity: 0.35 },
          "50%": { opacity: 0.75 },
        },
        "@keyframes agent-gyri-fade-2": {
          "0%, 100%": { opacity: 0.55 },
          "50%": { opacity: 0.25 },
        },
        "@keyframes agent-fissure-flow": {
          "0%, 100%": { strokeDashoffset: 0, opacity: 0.55 },
          "50%": { strokeDashoffset: -6, opacity: 0.85 },
        },
        "@keyframes agent-core-pulse": {
          "0%, 100%": { transform: "scale(1)", opacity: 1 },
          "50%": { transform: "scale(1.22)", opacity: 0.85 },
        },
        "@keyframes agent-halo-pulse": {
          "0%, 100%": { transform: "scale(1)", opacity: 0.25 },
          "50%": { transform: "scale(1.4)", opacity: 0.08 },
        },
        "@keyframes agent-flicker": {
          "0%, 96%, 100%": { opacity: 1 },
          "97%": { opacity: 0.55 },
          "98%": { opacity: 0.9 },
          "99%": { opacity: 0.6 },
        },
        "@keyframes agent-sweep": {
          "0%": { transform: "rotate(0deg)", opacity: 0 },
          "5%, 8%": { opacity: 0.5 },
          "15%, 100%": { opacity: 0, transform: "rotate(360deg)" },
        },
        "@keyframes agent-firing": {
          "0%, 90%, 100%": { opacity: 0 },
          "92%, 96%": { opacity: 1 },
        },
        "& .brain-outer": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-brain-breath 4.2s ease-in-out infinite"
            : "none",
        },
        "& .brain-mid": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-brain-breath-slow 5.6s ease-in-out infinite"
            : "none",
        },
        "& .brain-inner": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-brain-breath 3.4s ease-in-out infinite"
            : "none",
        },
        "& .fissure": {
          animation: active
            ? "agent-fissure-flow 4.2s ease-in-out infinite"
            : "none",
        },
        "& .gyri-L": {
          animation: active
            ? "agent-gyri-fade 3.6s ease-in-out infinite"
            : "none",
        },
        "& .gyri-R": {
          animation: active
            ? "agent-gyri-fade-2 4.4s ease-in-out infinite"
            : "none",
        },
        ...Object.fromEntries(
          particles.map((p, i) => [
            `& .pt-${i}`,
            {
              animation: active
                ? `agent-pt-${i} ${p.dur}s linear infinite`
                : "none",
            },
          ])
        ),
        "& .core-pulse": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-core-pulse 3.2s ease-in-out infinite"
            : "none",
        },
        "& .core-halo": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-halo-pulse 3.2s ease-in-out infinite"
            : "none",
        },
        "& .core-flicker": {
          animation: active ? "agent-flicker 6s ease-in-out infinite" : "none",
        },
        "& .sweep": {
          transformOrigin: "40px 40px",
          animation: active ? "agent-sweep 9s linear infinite" : "none",
        },
        "& .firing-1": {
          animation: active ? "agent-firing 5s ease-out infinite" : "none",
        },
        "& .firing-2": {
          animation: active ? "agent-firing 7s ease-out infinite 2.3s" : "none",
        },
        "& .firing-3": {
          animation: active ? "agent-firing 6s ease-out infinite 4.1s" : "none",
        },
      }}
    >
      {/* 뇌 실루엣 3겹 (외곽 / 중간 / 안쪽) — 와이어 두께감 */}
      <g className="brain-outer">
        <path
          d={BRAIN_OUTLINE}
          fill={primary}
          opacity="0.04"
          stroke={soft}
          strokeWidth="0.6"
          strokeOpacity="0.4"
        />
      </g>
      <g className="brain-mid">
        <path
          d={BRAIN_OUTLINE}
          fill="none"
          stroke={primary}
          strokeWidth="0.7"
          opacity="0.55"
        />
      </g>
      <g
        className="brain-inner"
        transform="translate(40 40) scale(0.93) translate(-40 -40)"
      >
        <path
          d={BRAIN_OUTLINE}
          fill="none"
          stroke={primary}
          strokeWidth="0.45"
          opacity="0.35"
          strokeDasharray="1.5 1.5"
        />
      </g>

      {/* sweep 라인 — 알아차림 스캔 */}
      <g className="sweep">
        <line
          x1="40"
          y1="40"
          x2="40"
          y2="10"
          stroke={primary}
          strokeWidth="0.4"
          opacity="0.45"
        />
      </g>

      {/* 좌우 hemisphere gyri (뇌주름) */}
      <g className="gyri-L" stroke={primary} strokeWidth="0.5" fill="none">
        {GYRI_LEFT.map((d, i) => (
          <path key={`gL-${i}`} d={d} />
        ))}
      </g>
      <g className="gyri-R" stroke={primary} strokeWidth="0.5" fill="none">
        {GYRI_RIGHT.map((d, i) => (
          <path key={`gR-${i}`} d={d} />
        ))}
      </g>

      {/* 종단 fissure (중앙 골) — 흐르는 dash */}
      <path
        className="fissure"
        d={BRAIN_FISSURE}
        fill="none"
        stroke={primary}
        strokeWidth="0.75"
        strokeDasharray="2 1.5"
        opacity="0.55"
      />

      {/* 신경 입자 16개 — 각자 고유한 chaotic trajectory (Lissajous 합성) */}
      {particles.map((p, i) => (
        <circle
          key={`pt-${i}`}
          className={`pt-${i}`}
          cx="40"
          cy="40"
          r={p.size}
          fill={primary}
          opacity={p.opacity}
        />
      ))}

      {/* synaptic firing — 가끔 깜빡이는 점 3개 */}
      <circle
        className="firing-1"
        cx="26"
        cy="32"
        r="1"
        fill={primary}
        opacity="0"
      />
      <circle
        className="firing-2"
        cx="54"
        cy="38"
        r="1.1"
        fill={primary}
        opacity="0"
      />
      <circle
        className="firing-3"
        cx="48"
        cy="52"
        r="0.9"
        fill={primary}
        opacity="0"
      />

      {/* 중심 코어 — 의식 */}
      <g className="core-flicker">
        <g className="core-halo">
          <circle cx="40" cy="40" r="13" fill={primary} opacity="0.07" />
        </g>
        <g className="core-halo">
          <circle cx="40" cy="40" r="8" fill={primary} opacity="0.14" />
        </g>
        <g className="core-pulse">
          <circle cx="40" cy="40" r="4.5" fill={primary} opacity="0.35" />
          <circle cx="40" cy="40" r="2.6" fill={primary} />
        </g>
      </g>
    </Box>
  );
}
